import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Badge,
  Card,
  CardBody,
  Row,
  Col,
  Button,
  CardHeader,
  CardTitle,
  Alert,
} from "reactstrap";
import moment from "moment";
import { useHistory, useLocation } from "react-router-dom";
import queryString from "query-string";
import { formatCurrency } from "../../../utils/formatCurrency";
import { ClipLoader } from "react-spinners";

import { useCompanies } from "hooks/companies";
import { useAuth } from "hooks/auth";
import { listarExtrato, listarCtesDoDia, ctesDoPedido, mensagemErro } from "services/meuFreteApi";

import ModalFilters from "../../../components/CustomModalFilters";
import ListCtes from "./components/ListCtes";
import TableCtes from "./components/TableCtes";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";

// PO1-3392 (decisão P4 do kickoff): o período é pela EMISSÃO do CT-e por
// padrão; a entrega da NF fica como opção. Mesmo padrão de MinhasFaturas.
export const DATA_CAMPOS = [
  { value: "emissao", label: "Emissão" },
  { value: "entrega", label: "Entrega" },
];
const CAMPO_DATA_PADRAO = "emissao";

const rotuloCampoData = (v) => (DATA_CAMPOS.find((o) => o.value === v) || DATA_CAMPOS[0]).label.toLowerCase();

// Linha da tabela a partir do CT-e do dia (listar-ctes) ou do pedido.
function linhaDoDia(item) {
  return {
    id: item.cte_id,
    numeroCte: item.cte_numero,
    totalFrete: formatCurrency(item.cte_valor_frete),
    numeroFatura: item.fat_situacao === 3 ? item.fat_numero || "Sem Número" : "Não Faturado",
    dataEmissao: moment(item.cte_data_emissao).format("DD/MM/YYYY"),
    notasFiscais: item.nf_numero,
  };
}

export function linhaDoPedido(c) {
  return {
    id: c.id,
    numeroCte: c.numero,
    notasFiscais: c.notas,
    dataEmissao: c.dataEmissao ? moment(c.dataEmissao).format("DD/MM/YYYY") : "—",
    totalFrete: formatCurrency(c.valorCobrado),
    numeroFatura: c.faturaSituacao === 3 ? c.faturaNumero || "Sem Número" : "Não Faturado",
  };
}

export default function Extrato() {
  const { empresa, transportadora, hasEmpresaSelection } = useCompanies();
  const { user } = useAuth();
  const history = useHistory();
  const location = useLocation();
  // PO1-3392: `?pedido=<token>` é o link do e-mail "Pedir análise à
  // transportadora" (H1). Com ele a tela mostra só os CT-es pedidos e o valor
  // cobrado; sem ele é o extrato de sempre. Ajuste PO1-3390 (07/10, Paulo/
  // Arneg): o valor pela nossa tabela NUNCA aparece para a transportadora.
  const tokenPedido = useMemo(() => {
    const q = queryString.parse(location.search || "");
    return typeof q.pedido === "string" && q.pedido ? q.pedido : null;
  }, [location.search]);

  // PO1-2286: para a transportadora, "Todos os embarcadores" vale (sem
  // empresaId o backend usa os vínculos ativos) e a transportadora é a do
  // JWT (raiz), então não precisa de seleção no topo.
  const ehTransportadora = user?.setor === "transportadora";
  const empresaId = empresa?.value;
  const transportadoraId = transportadora?.value;
  const podeConsultar = ehTransportadora
    ? Boolean(empresa)
    : Boolean(empresaId && transportadoraId);
  const paramsEscopo = useCallback(() => {
    const params = {};
    if (empresaId !== null && empresaId !== undefined) params.empresaId = empresaId;
    if (!ehTransportadora && transportadoraId) params.transportadoraId = transportadoraId;
    return params;
  }, [ehTransportadora, empresaId, transportadoraId]);

  const [modalFilters, setModalFilters] = useState(false);
  // Rascunho do modal (data*, campoData, divergencia) × aplicado (aux*, badges).
  const [dataInicial, setDataInicial] = useState(moment().startOf("month"));
  const [dataFinal, setDataFinal] = useState(moment().endOf("month"));
  const [campoData, setCampoData] = useState(CAMPO_DATA_PADRAO);
  const [divergencia, setDivergencia] = useState(false);
  const [auxDtInicial, setAuxDtInicial] = useState(moment().startOf("month"));
  const [auxDtFinal, setAuxDtFinal] = useState(moment().endOf("month"));
  const [auxCampoData, setAuxCampoData] = useState(CAMPO_DATA_PADRAO);
  const [auxDivergencia, setAuxDivergencia] = useState(false);
  const [badgeDataInicial, setBadgeDataInicial] = useState(
    moment().startOf("month").format("DD/MM/YYYY")
  );
  const [badgeDataFinal, setBadgeDataFinal] = useState(
    moment().endOf("month").format("DD/MM/YYYY")
  );

  const [listCtes, setListCtes] = useState([]);
  const [tableCtes, setTableCtes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataTableCtes, setDataTableCtes] = useState("");

  // Modo pedido
  const [pedido, setPedido] = useState(null); // { embarcador, total, pedidos, ctes }
  const [pedidoErro, setPedidoErro] = useState(null);
  const [pedidoLoading, setPedidoLoading] = useState(false);

  useEffect(() => {
    if (hasEmpresaSelection || (ehTransportadora && empresa)) {
      return;
    }

    setListCtes([]);
    setTableCtes([]);
    setDataTableCtes("");
    setLoading(false);
  }, [hasEmpresaSelection, ehTransportadora, empresa]);

  // PO1-3392: modo pedido. Não depende de período nem da empresa selecionada:
  // o token já diz embarcador, transportadora e CT-es (o escopo é conferido
  // no backend). Token expirado/inválido vira aviso e o extrato normal segue.
  useEffect(() => {
    let cancelado = false;
    async function carregarPedido() {
      if (!tokenPedido) {
        setPedido(null);
        setPedidoErro(null);
        return;
      }
      setPedidoLoading(true);
      setPedidoErro(null);
      try {
        const data = await ctesDoPedido(tokenPedido);
        if (!cancelado) setPedido(data);
      } catch (err) {
        if (cancelado) return;
        const code = err?.response?.data?.code;
        setPedido(null);
        setPedidoErro(
          code === "pedido_expirado"
            ? "Este link de pedido de análise expirou. Peça um novo ao cliente."
            : mensagemErro(err, "Não foi possível abrir o pedido de análise.")
        );
      } finally {
        if (!cancelado) setPedidoLoading(false);
      }
    }
    carregarPedido();
    return () => {
      cancelado = true;
    };
  }, [tokenPedido]);

  const paramsPeriodo = useCallback(
    (inicio, fim) => ({
      ...paramsEscopo(),
      dataInicial: moment(inicio).format("YYYY-MM-DD 00:00:00"),
      dataFinal: moment(fim).format("YYYY-MM-DD 23:59:59"),
      campoData: auxCampoData,
      ...(auxDivergencia ? { divergencia: 1 } : {}),
    }),
    [paramsEscopo, auxCampoData, auxDivergencia]
  );

  useEffect(() => {
    async function loadExtrato() {
      if (tokenPedido) return;
      if (!podeConsultar) {
        setListCtes([]);
        setTableCtes([]);
        setDataTableCtes("");
        return;
      }
      setLoading(true);
      try {
        setListCtes(await listarExtrato(paramsPeriodo(auxDtInicial, auxDtFinal)));
      } catch (_) {
        setListCtes([]);
      }
      setTableCtes([]);
      setLoading(false);
    }
    loadExtrato();
  }, [tokenPedido, podeConsultar, paramsPeriodo, auxDtInicial, auxDtFinal]);

  const handleDateInicial = useCallback((e) => {
    setDataInicial(e);
  }, []);

  const handleDateFinal = useCallback((e) => {
    setDataFinal(e);
  }, []);

  const toggleModalFilters = useCallback(() => {
    setModalFilters(!modalFilters);
  }, [modalFilters]);

  // Limpar volta tudo ao padrão: mês atual, por emissão, sem o filtro de
  // divergência. (Corrige o bug antigo: a data inicial ia para o FIM do mês e
  // a final aplicada nunca era resetada.)
  const onClearFilters = useCallback(() => {
    const inicio = moment().startOf("month");
    const fim = moment().endOf("month");
    setDataInicial(inicio);
    setDataFinal(fim);
    setCampoData(CAMPO_DATA_PADRAO);
    setDivergencia(false);
    setAuxDtInicial(inicio);
    setAuxDtFinal(fim);
    setAuxCampoData(CAMPO_DATA_PADRAO);
    setAuxDivergencia(false);
    setBadgeDataInicial(inicio.format("DD/MM/YYYY"));
    setBadgeDataFinal(fim.format("DD/MM/YYYY"));
  }, []);

  const loadTableCtes = useCallback(
    (date) => {
      async function loadTable() {
        if (!podeConsultar) {
          setTableCtes([]);
          setDataTableCtes("");
          return;
        }
        try {
          const itens = await listarCtesDoDia(paramsPeriodo(date, date));
          setTableCtes(itens.map(linhaDoDia));
          setDataTableCtes(moment(date).format("DD - MMM"));
        } catch (_) {
          setTableCtes([]);
        }
      }
      loadTable();
    },
    [podeConsultar, paramsPeriodo]
  );

  const onSubmit = useCallback(() => {
    setAuxDtInicial(dataInicial);
    setAuxDtFinal(dataFinal);
    setAuxCampoData(campoData);
    setAuxDivergencia(divergencia);
    setBadgeDataInicial(dataInicial.format("DD/MM/YYYY"));
    setBadgeDataFinal(dataFinal.format("DD/MM/YYYY"));
    setModalFilters(!modalFilters);
  }, [dataFinal, dataInicial, campoData, divergencia, modalFilters]);

  const verExtratoCompleto = useCallback(() => {
    history.push(location.pathname);
  }, [history, location.pathname]);

  const linhasPedido = useMemo(() => (pedido ? (pedido.ctes || []).map(linhaDoPedido) : []), [pedido]);

  // ─── Modo pedido (PO1-3392) ─────────────────────────────────────────────
  if (tokenPedido) {
    return (
      <div className="content">
        <Row>
          <Col lg="12" md="12" sm="12">
            <Card className="card-plain">
              <CardHeader>
                <Row>
                  <Col>
                    <CardTitle tag="h4">
                      <i className="tim-icons icon-paper" /> Extrato · Pedido de análise
                    </CardTitle>
                  </Col>
                  <Col className="text-right">
                    <Button className="btn-round btn-sm" color="info" onClick={verExtratoCompleto}>
                      Ver extrato completo
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
            </Card>
          </Col>
        </Row>
        {pedidoLoading ? (
          <div style={{ textAlign: "center" }}>
            <ClipLoader sizeUnit="px" size={90} color="#1d8cf8" loading />
            <br />
            <br />
            <h1 className="text-info" style={{ textAlign: "center" }}>
              Carregando ...
            </h1>
          </div>
        ) : null}
        {!pedidoLoading && pedidoErro ? (
          <Alert color="warning" data-testid="pedido-erro">
            {pedidoErro}
          </Alert>
        ) : null}
        {!pedidoLoading && pedido ? (
          <Row>
            <Col lg="12">
              <Card className="card-plain">
                <CardHeader>
                  <Row>
                    <Col>
                      <Badge style={{ backgroundColor: "#ffd600" }} data-testid="pedido-resumo">
                        {pedido.embarcador ? pedido.embarcador.nome : "O cliente"} pediu a análise de{" "}
                        {pedido.total} {pedido.total === 1 ? "CT-e" : "CT-es"}
                        {pedido.pedidos > pedido.total ? ` (${pedido.pedidos - pedido.total} fora do seu acesso)` : ""}
                      </Badge>
                      <p style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.85rem", opacity: 0.85 }}>
                        O cliente pede a explicação do cálculo do frete cobrado em cada CT-e abaixo: tabela aplicada,
                        peso e valor de mercadoria considerados, componentes e taxas. Responda pelo e-mail do pedido.
                      </p>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  <TableCtes data={linhasPedido} modoPedido />
                </CardBody>
              </Card>
            </Col>
          </Row>
        ) : null}
      </div>
    );
  }

  // ─── Extrato normal ─────────────────────────────────────────────────────
  if (!hasEmpresaSelection && !(ehTransportadora && empresa)) {
    return (
      <div className="content">
        <SpecificEmpresaRequiredNotice />
      </div>
    );
  }

  if (!ehTransportadora && !transportadoraId) {
    return (
      <div className="content">
        <SpecificEmpresaRequiredNotice
          message="Selecione uma transportadora específica para usar esta tela."
        />
      </div>
    );
  }

  return (
    <div className="content">
      <ModalFilters
        open={modalFilters}
        toggle={toggleModalFilters}
        handleDateInicial={handleDateInicial}
        dataInicial={dataInicial}
        handleDateFinal={handleDateFinal}
        dataFinal={dataFinal}
        onSubmit={() => onSubmit()}
        tipoData={campoData}
        onTipoData={setCampoData}
        opcoesTipoData={DATA_CAMPOS}
      >
        <div style={{ textAlign: "center", marginBottom: "0.75rem" }}>
          <Button
            type="button"
            size="sm"
            className="btn-round"
            color={divergencia ? "warning" : "secondary"}
            outline={!divergencia}
            aria-pressed={divergencia}
            onClick={() => setDivergencia((v) => !v)}
            title="Só CT-es cobrados acima da tabela de frete acordada"
          >
            {divergencia ? "✓ " : ""}Com divergência
          </Button>
        </div>
      </ModalFilters>
      <Row>
        <Col lg="12" md="12" sm="12">
          <Card className="card-plain">
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">
                    <i className="tim-icons icon-paper" /> Extrato
                  </CardTitle>
                </Col>
                <Col className="text-right">
                  <Button
                    className="btn-round btn-sm"
                    onClick={toggleModalFilters}
                    color="success"
                  >
                    Filtrar
                  </Button>
                  <Button
                    className="btn-round btn-sm"
                    color="danger"
                    onClick={onClearFilters}
                  >
                    Limpar
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col>
                  {badgeDataInicial || badgeDataFinal ? (
                    <>
                      <Badge color="info">
                        data inicial: {badgeDataInicial}
                      </Badge>
                      <Badge color="info">data final: {badgeDataFinal}</Badge>
                      <Badge color="info">por {rotuloCampoData(auxCampoData)}</Badge>
                      {auxDivergencia ? <Badge color="warning">com divergência</Badge> : null}
                    </>
                  ) : null}
                </Col>
              </Row>
            </CardHeader>
          </Card>
        </Col>
      </Row>
      {loading ? (
        <div style={{ textAlign: "center" }}>
          <ClipLoader
            sizeUnit="px"
            size={90}
            color="#1d8cf8"
            loading={loading}
          />
          <br />
          <br />
          <h1 className="text-info" style={{ textAlign: "center" }}>
            Carregando ...
          </h1>
        </div>
      ) : (
        <Row>
          <Col lg="5" md="5" sm="5">
            <Card className="card-timeline card-plain">
              <CardBody>
                <ListCtes
                  listCtes={listCtes}
                  tableCtes={(date) => loadTableCtes(date)}
                />
              </CardBody>
            </Card>
          </Col>
          <Col lg="7" md="7" sm="7">
            <Card className="card-plain">
              <CardHeader>
                <Row>
                  <Col>
                    <Badge style={{ backgroundColor: "#ffd600" }}>
                      Lista de ctes do dia: {dataTableCtes}
                    </Badge>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <TableCtes data={tableCtes} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
}
